<template>
    <div class="pa-5">
        <v-card>
            <v-card-title>
                <h1 class="title">{{ 'Connect Devices' | term }}</h1>
                <v-btn small color="secondary" class="ml-3" @click="openCreate()" v-if="!!selectedDepartment">
                    <v-icon left>mdi-plus</v-icon>Add
                </v-btn>
                <v-spacer></v-spacer>
                <v-select
                    v-if="departments"
                    :items="departments"
                    item-value="id"
                    item-text="name"
                    v-model="selectedDepartment"
                    label="Department"
                    single-line
                    hide-details
                    class="admin-table-filter"
                    :menu-props="{ dark: true }"
                >
                    <template v-slot:item="data">
                        <template v-if="data.item.isGroup">
                            <v-list-item-content
                                class="caption"
                                @click="$event.stopPropagation()"
                                v-text="data.item.name"
                            ></v-list-item-content>
                        </template>
                        <template v-else>
                            <v-list-item-content class="pl-5" v-text="data.item.name"></v-list-item-content>
                        </template>
                    </template>
                </v-select>
            </v-card-title>

            <v-data-table
                v-if="!!selectedDepartment"
                :headers="headers"
                :items="devices"
                disable-pagination
                hide-default-footer
            >
                <template v-slot:item.deviceName="{ item }">
                    <div class="d-flex align-center">
                        <span>{{ item.deviceName }}</span>
                    </div>
                </template>
                <template v-slot:item.codeExpires="{ item }">
                    <span :class="{ 'red--text': isExpired(item) }">
                        {{ formatExpiry(item.codeExpires) }}
                    </span>
                </template>
                <template v-slot:item.code="{ item }">
                    <div class="d-flex align-center">
                        <span class="mr-2">{{ item.code || '' }}</span>
                        <v-btn v-if="item.code" icon small @click="copyCode(item.code)">
                            <v-icon>mdi-content-copy</v-icon>
                        </v-btn>
                    </div>
                </template>
                <template v-slot:item.actions="{ item }">
                    <div class="d-flex align-center justify-end">
                        <v-btn small text color="primary" class="mr-1" @click="openEdit(item)">
                            <v-icon left small>mdi-pencil</v-icon>Edit
                        </v-btn>
                        <v-btn small text color="primary" @click="refreshCode(item)">
                            <v-icon left small>mdi-refresh</v-icon>Code
                        </v-btn>
                    </div>
                </template>
            </v-data-table>

            <empty-state
                v-if="!selectedDepartment"
                icon="mdi-alert-circle-outline"
                :message="'Select a ' + $termSync('Department')"
            ></empty-state>
        </v-card>

        <!-- Create Dialog -->
        <standard-dialog :value="showCreate" :max-width="500" persistent>
            <template v-slot:title>
                Create <span class="ml-1">{{ 'Connect Device' | term }}</span>
            </template>
            <template v-slot:content>
                <v-form ref="createForm" :lazy-validation="true">
                    <v-container>
                        <v-row>
                            <v-col cols="12" class="pa-2">
                                <v-text-field
                                    v-model="createModel.deviceName"
                                    :rules="[$rules.required(), $rules.maxLength(80)]"
                                    label="Name"
                                    autocomplete="off"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" class="pa-2">
                                <v-checkbox v-model="createModel.createRegistrationCode" label="Generate Registration Code Now"></v-checkbox>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
            </template>
            <template v-slot:actions>
                <v-spacer></v-spacer>
                <v-btn color="gray" text @click="showCreate = false">Cancel</v-btn>
                <v-btn color="primary" text @click="create()">Create</v-btn>
            </template>
            <template v-slot:offline-actions>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showCreate = false'>Cancel</v-btn>
                <offline-btn text></offline-btn>
            </template>
        </standard-dialog>

        <!-- Edit Dialog -->
        <standard-dialog :value="showEdit" :max-width="500" persistent>
            <template v-slot:title>
                Edit <span class="ml-1">{{ 'Connect Device' | term }}</span>
            </template>
            <template v-slot:content>
                <v-form ref="editForm" :lazy-validation="true">
                    <v-container>
                        <v-row>
                            <v-col cols="12" class="pa-2">
                                <v-text-field
                                    v-model="editModel.deviceName"
                                    :rules="[$rules.required(), $rules.maxLength(500)]"
                                    label="Name"
                                    autocomplete="off"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
            </template>
            <template v-slot:actions>
                <v-btn color='red' text v-if="!!editModel && !!editModel.id" @click="delEdit()">Delete</v-btn>
                <v-spacer></v-spacer>
                <v-btn color="gray" text @click="showEdit = false">Cancel</v-btn>
                <v-btn color="primary" text @click="saveEdit()">Save</v-btn>
            </template>
            <template v-slot:offline-actions>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showEdit = false'>Cancel</v-btn>
                <offline-btn text></offline-btn>
            </template>
        </standard-dialog>

        <confirm ref="confirm"></confirm>
    </div>
</template>

<script>
import MineAreas from '@/lib/data/MineAreas';
import ConnectDevices from '@/lib/data/ConnectDevices';
import { EventBus, Events } from '@/lib/EventBus';

export default {
    data() {
        return {
            mineAreas: null,
            departments: [],
            selectedDepartment: null,
            devices: [],
            showCreate: false,
            showEdit: false,
            editModel: {
                id: null,
                deviceName: '',
            },
            createModel: {
                deviceName: '',
                createRegistrationCode: true,
            },
            headers: [
                { text: 'Name', value: 'deviceName' },
                { text: 'Code Expires', value: 'codeExpires' },
                { text: 'Code', value: 'code' },
                { text: '', value: 'actions', align: 'end', sortable: false },
            ],
        };
    },
    async created() {
        await this.loadDepartments();
    },
    watch: {
        selectedDepartment: {
            async handler(val) {
                if (val) await this.loadDevices();
                else this.devices = [];
            },
        },
    },
    methods: {
        async loadDepartments() {
            const mineAreas = await MineAreas.get(['Departments']);

            var departments = [];
            for (let mine of mineAreas) {
                departments.push({ id: mine.id, name: mine.name ?? '', isGroup: true, mineAreaId: mine.id });
                for (let dept of mine.departments) {
                    departments.push({
                        id: dept.id,
                        mineAreaId: mine.id,
                        name: dept.name ?? '',
                        isGroup: false,
                    });
                }
            }
            this.departments = departments;
        },
        async loadDevices() {
            const list = await ConnectDevices.getDevicesByDepartment(this.selectedDepartment);
            this.devices = list || [];
        },
        openCreate() {
            this.createModel = { deviceName: '', createRegistrationCode: true };
            this.showCreate = true;
            this.$nextTick(() => this.$refs.createForm && this.$refs.createForm.resetValidation());
        },
        async create() {
            if (!this.$refs.createForm.validate()) return;
            const command = {
                _type: 'CreateOrdoConnectDeviceRegistrationCommand',
                departmentId: this.selectedDepartment,
                deviceName: this.createModel.deviceName,
                createRegistrationCode: !!this.createModel.createRegistrationCode,
            };
            await ConnectDevices.createDeviceRegistration(this.selectedDepartment, command);
            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Connect Device')} Created`);
            this.showCreate = false;
            await this.loadDevices();
        },
        openEdit(item) {
            this.editModel = {
                id: item.deviceId,
                deviceName: item.deviceName || '',
            };
            this.showEdit = true;
            this.$nextTick(() => this.$refs.editForm && this.$refs.editForm.resetValidation());
        },
        async saveEdit() {
            if (!this.$refs.editForm.validate()) return;
            const trimmed = (this.editModel.deviceName || '').trim();
            const command = {
                _type: 'UpdateOrdoConnectDeviceRegistrationCommand',
                id: this.editModel.id,
                deviceName: trimmed,
            };
            await ConnectDevices.updateDeviceRegistration(this.selectedDepartment, command);
            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Connect Device')} Updated`);
            this.showEdit = false;
            await this.loadDevices();
        },
        async delEdit() {
            if (!(await this.$refs.confirm.openAsDeleteResource(this.$termSync('Connect Device'), {}))) return;
            await ConnectDevices.deleteDeviceRegistration(this.selectedDepartment, this.editModel.id);
            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Connect Device')} Deleted`);
            this.showEdit = false;
            await this.loadDevices();
        },
        async refreshCode(item) {
            await ConnectDevices.refreshDeviceRegistrationCode(this.selectedDepartment, item.deviceId);
            EventBus.$emit(Events.ToastSuccess, 'Code Refreshed');
            await this.loadDevices();
        },
        async del(item) {
            if (await this.$refs.confirm.openAsDeleteResource(this.$termSync('Connect Device'), {})) {
                await ConnectDevices.deleteDeviceRegistration(this.selectedDepartment, item.deviceId);
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Connect Device')} Deleted`);
                await this.loadDevices();
            }
        },
        isExpired(item) {
            if (!item || !item.codeExpires) return false;
            const exp = new Date(item.codeExpires);
            return new Date() > exp;
        },
        formatExpiry(value) {
            if (!value) return '';
            const d = new Date(value);
            if (isNaN(d.getTime())) return '';
            return d.toLocaleString();
        },
        async copyCode(text) {
            try {
                if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(text);
                } else {
                    const ta = document.createElement('textarea');
                    ta.value = text;
                    document.body.appendChild(ta);
                    ta.select();
                    document.execCommand('copy');
                    document.body.removeChild(ta);
                }
                EventBus.$emit(Events.ToastSuccess, 'Copied to clipboard');
            } catch {
                EventBus.$emit(Events.ToastError, 'Failed to copy');
            }
        },
    },
};
</script>
